import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { useAtom, useSetAtom } from 'jotai'

import { ExcelPlanResponse, OptimizePlanRequest, OptimizePlanResponse, ProductionPlanService } from '@/api'
import { normalizeOptimizePlanResponse } from '@/utils/normalizeOptimizeResponse'
import { excelPlanResponseAtom, optimizePlanRequestAtom, optimizePlanResponseAtom, originalPlanResponseAtom } from '@/atoms/optimization'

/**
 * Encapsulates the CreatePlan wizard state transitions across all steps.
 */
export const useCreatePlanFlow = () => {
  const navigate = useNavigate()
  const [stepIndex, setStepIndex] = useState(0)
  const [excelPlanResponse, setExcelPlanResponse] = useAtom(excelPlanResponseAtom)
  const [optimizeRequest, setOptimizeRequest] = useAtom(optimizePlanRequestAtom)
  const setOriginalPlanResponse = useSetAtom(originalPlanResponseAtom)
  const setOptimizeResponse = useSetAtom(optimizePlanResponseAtom)

  const optimizeMutation = useMutation({
    mutationFn: (req: OptimizePlanRequest) => ProductionPlanService.optimize(req),
    onSuccess: (data) => {
      setOriginalPlanResponse(normalizeOptimizePlanResponse(data))
    },
  })

  const steps = useMemo(
    () => [
      { label: 'Excelデータ読み込み' },
      { label: '範囲・日程' },
      { label: '工場稼働設定' },
      { label: '最適化' },
    ],
    [],
  )

  const goToStep = useCallback((index: number) => {
    setStepIndex(index)
  }, [setStepIndex])

  const loadExcel = useCallback(
    (data: ExcelPlanResponse) => {
      setExcelPlanResponse(data)
      setStepIndex(1)
    },
    [setExcelPlanResponse, setStepIndex],
  )

  const applySetting = useCallback(
    async (req: OptimizePlanRequest) => {
      await optimizeMutation.mutateAsync(req)
      setOptimizeRequest(req)
      setStepIndex(2)
    },
    [optimizeMutation, setOptimizeRequest, setStepIndex],
  )

  const applyCapacity = useCallback(
    (req: OptimizePlanRequest) => {
      setOptimizeRequest(req)
      setStepIndex(3)
    },
    [setOptimizeRequest, setStepIndex],
  )

  const finalizeOptimization = useCallback(
    (response: OptimizePlanResponse) => {
      setOptimizeResponse(normalizeOptimizePlanResponse(response))
      navigate({ to: '/plan' })
    },
    [navigate, setOptimizeResponse],
  )

  return {
    stepIndex,
    steps,
    excelPlanResponse,
    optimizeRequest,
    optimizeMutation,
    goToStep,
    loadExcel,
    applySetting,
    applyCapacity,
    finalizeOptimization,
  }
}
